// src/lib/pharmacyFirst/pathways/aht/_shared/foundayo-supply-downstream.ts
//
// AHT Weight Management — Orforglipron (Foundayo®) shared supply downstream.
// PGD pre-supply checks, contraception and HRT counselling, supply attestation and the shared outcomes
// used by the initial, transfer and re-initiation pathways. Source: AHT Foundayo PGD v2.1.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

export const FDY_OUT_CANNOT_SUPPLY = "FDY_OUT_CANNOT_SUPPLY";
export const FDY_OUT_SUPPLY = "FDY_OUT_SUPPLY";

export interface FoundayoDownstreamOptions {
  idPrefix: string;
  attestHeading: string;
  /** The first attestation lines, specific to the route (stock, strength and imprint check). */
  attestLead: string[];
  supplyTitle: string;
  supplyReason: string;
  /** Extra safety-netting lines specific to the route. */
  extraSafetyNetting?: string[];
}

export interface FoundayoDownstream {
  entryNodeId: string;
  nodes: PathwayNode[];
  outcomes: Record<string, OutcomeNode>;
}

export function buildFoundayoSupplyDownstream(opts: FoundayoDownstreamOptions): FoundayoDownstream {
  const pgdChecks = `${opts.idPrefix}-pgd-fresh-checks`;
  const intContraception = `${opts.idPrefix}-int1-oral-contraception`;
  const intHrt = `${opts.idPrefix}-int2-hrt-progestogen`;
  const attest = `${opts.idPrefix}-dose-confirm`;

  const nodes: PathwayNode[] = [
    {
      id: pgdChecks,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-foundayo",
      anyTriggered: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
      allCleared: intContraception,
    },

    {
      id: intContraception,
      type: "yes_no",
      text: "Is the patient using oral hormonal contraception?",
      hint: "Orforglipron may make oral hormonal contraception less effective. Advise a non-oral method, or add a barrier method, for 30 days after initiation, for 30 days after every dose increase, and after any restart.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the contraceptive counselling: non-oral method or added barrier method for 30 days after initiation, after every dose increase and after a restart",
        acknowledgementText: "Patient has been counselled on contraceptive reliability and the 30-day barrier or non-oral method requirement",
      },
      yes: intHrt,
      no: intHrt,
    },

    {
      id: intHrt,
      type: "yes_no",
      text: "Is the patient taking an oral progestogen as part of hormone replacement therapy (HRT)?",
      hint: "Do not adjust HRT under this PGD. Refer the patient to the HRT prescriber before or soon after starting and at each dose increase, and document this.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document that the patient has been advised to speak to their HRT prescriber, and when",
        acknowledgementText: "Patient has been advised to contact the HRT prescriber before or soon after starting and at each dose increase",
      },
      yes: attest,
      no: attest,
    },

    {
      id: attest,
      type: "attestation",
      text: opts.attestHeading,
      attestationStatement:
        "I confirm that I have:\n" +
        opts.attestLead.map((line) => `• ${line}\n`).join("") +
        "• Confirmed storage: original package, protected from light; no cold chain\n" +
        "• Counselled the patient: one tablet once daily at any time of day, with or without food, swallowed whole (not broken, crushed or chewed); never more than one tablet in a day\n" +
        "• Counselled the patient on missed dose guidance: resume as soon as possible, never double up\n" +
        "• Explained that the dose is increased no more often than every 30 days, one step at a time\n" +
        "• Counselled on the pancreatitis, vision change, mood and fluid intake warnings\n" +
        "• Advised the patient to tell any anaesthetist, surgeon, dentist or endoscopist before a procedure\n" +
        "• Explained GP notification and recorded the consent decision. Where consent was declined or there is no GP, the risk based assessment is recorded\n" +
        "• Verified the medical history with the GP or the clinical record, or recorded what other steps were taken\n" +
        "• Provided the patient information leaflet, explained the black triangle status and Yellow Card reporting\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: FDY_OUT_SUPPLY } },
        optionB: { label: "Do not supply", next: { outcomeId: FDY_OUT_CANNOT_SUPPLY } },
      },
    },
  ];

  const outcomes: Record<string, OutcomeNode> = {
    [FDY_OUT_CANNOT_SUPPLY]: {
      id: FDY_OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — Foundayo® (orforglipron)",
      reason:
        "An inclusion criterion was not met or an exclusion criterion was triggered, or the practitioner declined supply on clinical judgement. Supply cannot proceed under this PGD.",
      safetyNetting: [
        "Discuss the reasons for exclusion with the patient and offer advice.",
        "Refer to the GP where a clinical assessment, investigation or prescribing decision is needed, including any dose change to an interacting medicine.",
        "Provide lifestyle support advice and signpost to alternative weight management options.",
        "Document the reason for exclusion and the action taken.",
        "If the reason is a suspected or known eating disorder, refer to the GP with the patient's consent and signpost to eating disorder support. Do not simply decline and close the consultation.",
        "If the reason is the GP information-sharing risk assessment, explain why, record the reason, and signpost to GP registration or a prescriber with access to the full medical record.",
      ],
    },
    [FDY_OUT_SUPPLY]: {
      id: FDY_OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: opts.supplyTitle,
      reason: opts.supplyReason,
      drugRef: "foundayo",
      safetyNetting: [
        "Return in 4 weeks for monthly monitoring review.",
        ...(opts.extraSafetyNetting ?? []),
        "One tablet once daily, swallowed whole. Never take more than one tablet in a day and never double up on a missed dose.",
        "Contact the service immediately for any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
        "Seek urgent medical attention for signs of pancreatitis: severe, persistent abdominal pain radiating to the back, with nausea and vomiting.",
        "Report any sudden change or loss of vision immediately.",
        "Report any new or worsening low mood or anxiety, or any thoughts of self harm, for same day medical assessment.",
        "Maintain fluid intake, especially with vomiting or diarrhoea. If fluids cannot be kept down, seek same day assessment.",
        "Store in the original package, protected from light. No refrigeration required.",
      ],
    },
  };

  return { entryNodeId: pgdChecks, nodes, outcomes };
}

export function foundayoRedirectOutcome(
  id: string,
  title: string,
  reason: string,
  safetyNetting: string[],
  outcomeType: "cannot_supply" | "refer_routine" = "cannot_supply",
): OutcomeNode {
  return { id, type: "outcome", outcomeType, title, reason, safetyNetting };
}
